import { useState } from "react";
import { Controller } from "react-hook-form";
import { capitalize } from "lodash";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Textarea } from "@/app/admin/_common/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/admin/_common/ui/popover";
import CuoButton from "@/app/admin/_common/CuoButton";
import { cn } from "@/app/_utils/cn";
import { ChevronDown, Plus, ImagePlus } from "lucide-react";
import type { LinkTaxonomy } from "./useLinkTaxonomy";

interface LinkInputProps {
  control: any;
  errors: any;
}

function TextInput({
  control,
  errors,
  name,
  label,
  placeholder,
}: LinkInputProps & { name: string; label: string; placeholder: string }) {
  return (
    <div className="grid gap-3">
      <Label htmlFor={name}>{label}</Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Input id={name} {...field} value={field.value ?? ""} placeholder={placeholder} />
        )}
      />
      {errors[name] && <span className="text-[12px] text-red-600">{errors[name]?.message}</span>}
    </div>
  );
}

/**
 * Pick an existing medium/source or type a new one. Replaces the MUI Autocomplete
 * + createFilterOptions pattern: same popover + filtered list as LocationInput in
 * shows/[show_id]/details, with a trailing `Add "<query>"` row for free text.
 */
function AddableCombobox({
  control,
  errors,
  name,
  label,
  placeholder,
  options,
}: LinkInputProps & {
  name: string;
  label: string;
  placeholder: string;
  options: LinkTaxonomy[];
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const query = search.trim();
  const filtered = options.filter((o) =>
    (o.name ?? "").toLowerCase().includes(query.toLowerCase())
  );
  const canAdd =
    query.length > 0 && !options.some((o) => o.name?.toLowerCase() === query.toLowerCase());

  return (
    <div className="grid gap-3">
      <Label htmlFor={name}>{label}</Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          function select(value: string) {
            field.onChange(value);
            setSearch("");
            setOpen(false);
          }

          return (
            <Popover
              open={open}
              onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearch("");
              }}
            >
              <PopoverTrigger asChild>
                <button
                  id={name}
                  type="button"
                  className="flex h-[30px] w-full items-center justify-between rounded-md border border-input bg-card px-3 text-[12.5px] text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="truncate">
                    {field.value ? capitalize(field.value) : placeholder}
                  </span>
                  <ChevronDown className="size-3.5 flex-none text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[320px] p-2">
                <Input
                  autoFocus
                  placeholder={`Search ${label.toLowerCase()}s…`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mb-2"
                />
                <div className="max-h-64 overflow-y-auto">
                  {filtered.length === 0 && !canAdd ? (
                    <div className="px-2 py-3 text-center text-[12px] text-muted-foreground">
                      No {label.toLowerCase()}s found
                    </div>
                  ) : (
                    <>
                      {filtered.map((option) => (
                        <button
                          key={option.name}
                          type="button"
                          onClick={() => select(option.name)}
                          className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-[12.5px] hover:bg-muted"
                        >
                          {capitalize(option.name)}
                        </button>
                      ))}
                      {canAdd && (
                        <button
                          type="button"
                          onClick={() => select(query)}
                          className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-[12.5px] text-foreground hover:bg-muted"
                        >
                          <Plus className="size-3.5 flex-none text-muted-foreground" />
                          Add “{query}”
                        </button>
                      )}
                    </>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          );
        }}
      />
      {errors[name] && <span className="text-[12px] text-red-600">{errors[name]?.message}</span>}
    </div>
  );
}

function TextareaInput({
  control,
  errors,
  name,
  label,
  placeholder,
  maxLength,
}: LinkInputProps & {
  name: string;
  label: string;
  placeholder: string;
  maxLength: number;
}) {
  return (
    <div className="grid gap-3">
      <Label htmlFor={name}>{label}</Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <Textarea
              id={name}
              {...field}
              value={field.value ?? ""}
              placeholder={placeholder}
              rows={3}
              className="text-[12.5px]"
            />
            <span className="text-[11.5px] text-muted-foreground">
              {(field.value ?? "").length}/{maxLength}
            </span>
          </>
        )}
      />
      {errors[name] && <span className="text-[12px] text-red-600">{errors[name]?.message}</span>}
    </div>
  );
}

export const NameInput = (props: LinkInputProps) => (
  <TextInput {...props} name="name" label="Name" placeholder="Link name" />
);

export const RedirectInput = (props: LinkInputProps) => (
  <TextInput
    {...props}
    name="redirect"
    label="Redirect"
    placeholder="https://comedyuo.com/shows"
  />
);

export const SourceInput = (props: LinkInputProps & { options: LinkTaxonomy[] }) => (
  <AddableCombobox {...props} name="source" label="Source" placeholder="Select or add a source" />
);

export const MediumInput = (props: LinkInputProps & { options: LinkTaxonomy[] }) => (
  <AddableCombobox {...props} name="medium" label="Medium" placeholder="Select or add a medium" />
);

export const PreviewTitleInput = (props: LinkInputProps) => (
  <TextInput
    {...props}
    name="preview_title"
    label="Preview title"
    placeholder="Leave blank to use the linked show"
  />
);

export const PreviewDescriptionInput = (props: LinkInputProps) => (
  <TextareaInput
    {...props}
    name="preview_description"
    label="Preview description"
    placeholder="Leave blank to use the linked show"
    maxLength={300}
  />
);

/**
 * Share-card image picker. Reads the file as a data URL; the service uploads it
 * to GCS on save. `onSelect("")` clears an existing image.
 */
export const PreviewImageInput = ({
  currentImage,
  onSelect,
}: {
  currentImage?: string | null;
  onSelect: (dataUrl: string | null) => void;
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [cleared, setCleared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1000 * 1024) {
      setError("Image is too large (15mb or less)");
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPreview(result);
      setCleared(false);
      onSelect(result);
    };
    reader.readAsDataURL(file);
  }

  function handleRemove() {
    setPreview(null);
    setCleared(true);
    onSelect("");
  }

  const displaySrc = preview ?? (cleared ? null : currentImage);

  return (
    <div className="grid gap-3">
      <Label>Preview image</Label>
      {displaySrc && (
        <img
          src={displaySrc}
          alt="Link share preview"
          className="aspect-[1.91/1] w-full rounded-md border border-border object-cover"
        />
      )}
      <label
        className={cn(
          "flex h-[38px] cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/40 text-[12.5px] text-muted-foreground hover:bg-muted",
          error && "border-red-600 text-red-600"
        )}
      >
        <ImagePlus className="size-3.5" />
        Upload image
        <input
          type="file"
          accept="image/png, image/jpeg, image/webp"
          className="hidden"
          onChange={handleChange}
        />
      </label>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11.5px] text-muted-foreground">Recommended 1200×630</span>
        {displaySrc && (
          <CuoButton variant="ghost" label="Remove" onClick={handleRemove} className="h-6 px-2" />
        )}
      </div>
      {error && <span className="text-[12px] text-red-600">{error}</span>}
    </div>
  );
};
